import React, { useState } from "react";
import { Send, Plus, Loader2, Save, CheckCircle2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { BILLER_ROLES, TRANSMITTAL_BADGE } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const cls = "bg-slate-950 border-slate-700";

function PersonSelect({ value, onChange, people, disabled, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid} className={cls}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
        {people.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Acknowledge({ tr }) {
  if (tr.status === "Acknowledged") {
    const a = tr.acknowledgement || {};
    return (
      <div data-testid="transmittal-ack" className="mt-6 flex gap-3 items-start bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 text-sm">
        <CheckCircle2 size={18} className="text-emerald-300 mt-0.5" />
        <div>Received and acknowledged by <b>{a.by_name}</b> on {new Date(a.at).toLocaleString()}.
          {a.remarks && <div className="text-slate-400 mt-1">{a.remarks}</div>}</div>
      </div>
    );
  }
  return tr.status === "Transmitted" ? (
    <div data-testid="transmittal-waiting" className="mt-6 text-xs text-amber-300">
      Waiting for {tr.assigned_biller?.name} to receive and acknowledge in Finance → Billing Transmittals.
    </div>
  ) : null;
}

export function TransmittalPanel({ sh, users, user, onSaved }) {
  const tr = sh.transmittal || {};
  const locked = tr.status === "Acknowledged";
  const docs0 = tr.documents?.length ? tr.documents : (sh.documentation?.requirements || []).map((r) => ({ name: r.name, included: !!r.submitted }));
  const [f, setF] = useState({
    turnover_date: tr.turnover_date || "", delivered_by_id: tr.delivered_by?.id || user.id,
    assigned_biller_id: tr.assigned_biller?.id || "", notes: tr.notes || "",
  });
  const [docs, setDocs] = useState(docs0);
  const [newDoc, setNewDoc] = useState("");
  const [busy, setBusy] = useState("");

  const save = async (send) => {
    setBusy(send ? "send" : "save");
    try {
      await api.post(`/shipments/${sh.id}/transmittal`, { ...f, send, documents: docs });
      toast.success(send ? "Transmitted to billing" : "Draft saved"); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="transmittal-panel" className="glass-card rounded-lg p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 font-display text-xl font-bold"><Send size={18} className="text-sky-400" /> Billing Transmittal Form</div>
        <div className="flex items-center gap-2">
          {tr.transmittal_number && <span data-testid="transmittal-number" className="font-mono-ops text-xs text-slate-400">{tr.transmittal_number}</span>}
          <span data-testid="transmittal-status" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${TRANSMITTAL_BADGE[tr.status || "Draft"]}`}>{tr.status || "Not started"}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>MAWB #</Label><Input data-testid="transmittal-mawb" readOnly value={tr.mawb_number || sh.documentation?.mawb_number || sh.pre_alert?.mawb_hint || ""} className={`${cls} font-mono-ops`} /></div>
        <div><Label>Date of turnover to biller</Label>
          <Input data-testid="input-turnover-date" type="date" value={f.turnover_date} disabled={locked} onChange={(e) => setF({ ...f, turnover_date: e.target.value })} className={cls} /></div>
        <div><Label>Delivered to billing by</Label>
          <PersonSelect testid="select-delivered-by" value={f.delivered_by_id} onChange={(v) => setF({ ...f, delivered_by_id: v })} people={users} disabled={locked} placeholder="Who hands it over" /></div>
        <div><Label>Assigned biller (receives & acknowledges)</Label>
          <PersonSelect testid="select-assigned-biller" value={f.assigned_biller_id} onChange={(v) => setF({ ...f, assigned_biller_id: v })}
                        people={users.filter((u) => BILLER_ROLES.includes(u.role))} disabled={locked} placeholder="Choose biller" /></div>
      </div>

      <div className="mt-6">
        <div>
          <div className="font-display text-lg font-bold mb-2">Documents turned over</div>
          <div className="space-y-2">
            {docs.length === 0 && <div className="text-xs text-slate-500">No documents listed yet.</div>}
            {docs.map((d, i) => (
              <label key={i} className="flex items-center gap-3 bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-sm">
                <Checkbox data-testid={`tr-doc-${i}`} checked={d.included} disabled={locked} onCheckedChange={() => setDocs(docs.map((x, idx) => (idx === i ? { ...x, included: !x.included } : x)))} />
                {d.name}
              </label>
            ))}
          </div>
          {!locked && (
            <div className="flex gap-2 mt-2">
              <Input data-testid="input-tr-new-doc" value={newDoc} onChange={(e) => setNewDoc(e.target.value)} placeholder="Add document…" className={cls} />
              <Button variant="outline" onClick={() => { if (newDoc) { setDocs([...docs, { name: newDoc, included: true }]); setNewDoc(""); } }} className="border-slate-700 hover:bg-slate-800"><Plus size={14} /></Button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4"><Label>Notes</Label>
        <Textarea data-testid="input-tr-notes" rows={2} value={f.notes} disabled={locked} onChange={(e) => setF({ ...f, notes: e.target.value })} className={cls} /></div>
      {!locked && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="button-save-transmittal" variant="outline" onClick={() => save(false)} disabled={!!busy} className="border-slate-700 hover:bg-slate-800">
            {busy === "save" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save draft
          </Button>
          <Button data-testid="button-send-transmittal" onClick={() => save(true)} disabled={!!busy} className="bg-blue-600 hover:bg-blue-500">
            {busy === "send" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />} {tr.status === "Transmitted" ? "Update & resend" : "Transmit to billing"}
          </Button>
        </div>
      )}
      <Acknowledge tr={tr} />
    </div>
  );
}
